/**
 * PreventionScoringService.ts - Prevention score calculation logic
 *
 * Prevention scores represent how well a response prevents negative outcomes.
 * They convert impact values (-100 to +100) to prevention scores (0 to 100).
 *
 * Uses an asymmetric formula that penalizes risky choices more heavily:
 * - Negative impacts: preventionScore = 50 + (impact / 1.3)
 * - Positive impacts: preventionScore = 50 + (impact / 2)
 *
 * This means risky choices (negative impacts) drop scores faster than
 * good choices raise them, encouraging safer decision-making.
 */

import {
  CategoryPreventionScore,
  IncidentPreventionResult,
  CumulativePreventionScores,
  PreventionScoringWeights
} from '../types/PreventionScore';
import { ScoringWeights } from '../types/ScoringConfiguration';
import { Incident, ResponseOption, IncidentResponse } from '../types/Incident';

/**
 * Default scoring weights if none provided
 */
const DEFAULT_WEIGHTS: PreventionScoringWeights = {
  security: 0.25,
  reputation: 0.25,
  financial: 0.25,
  operational: 0.25
};

/**
 * Convert a single impact value (-100 to +100) to a prevention score (0-100)
 *
 * Uses an asymmetric formula that penalizes negative impacts more heavily:
 * - Negative impacts (risky choices) are divided by 1.3 instead of 2
 * - Positive impacts remain at /2
 *
 * Examples:
 * - Impact -100 → score 0 (clamped from -27)
 * - Impact -50 → score 12 (was 25, more penalty)
 * - Impact -30 → score 27 (was 35, more penalty)
 * - Impact 0 → score 50 (neutral, same)
 * - Impact +50 → score 75 (same)
 * - Impact +100 → score 100 (same)
 *
 * @param impact - Impact value from -100 to +100
 * @returns Prevention score from 0 to 100
 */
export function calculatePreventionScore(impact: number): number {
  let score: number;
  if (impact < 0) {
    // Penalize risky/negative choices more heavily
    score = 50 + (impact / 1.3);
  } else {
    // Positive choices remain the same
    score = 50 + (impact / 2);
  }
  return Math.max(0, Math.min(100, Math.round(score)));
}

/**
 * Calculate prevention scores for all categories from response impact
 *
 * @param impact - Impact object with security, reputation, financial, operational values
 * @returns CategoryPreventionScore with 0-100 values for each category
 */
export function calculateCategoryPreventionScores(
  impact: { security: number; reputation: number; financial: number; operational: number }
): CategoryPreventionScore {
  return {
    security: calculatePreventionScore(impact.security),
    reputation: calculatePreventionScore(impact.reputation),
    financial: calculatePreventionScore(impact.financial),
    operational: calculatePreventionScore(impact.operational)
  };
}

/**
 * Calculate weighted overall prevention score from category scores
 *
 * @param categoryScores - Prevention scores for each category (0-100)
 * @param weights - Optional custom weights (will be normalized)
 * @returns Weighted average prevention score (0-100)
 */
export function calculateOverallPreventionScore(
  categoryScores: CategoryPreventionScore,
  weights?: ScoringWeights | PreventionScoringWeights
): number {
  const w = weights || DEFAULT_WEIGHTS;

  // Normalize weights to sum to 1
  const totalWeight = w.security + w.reputation + w.financial + w.operational;
  const normalizedWeights = {
    security: w.security / totalWeight,
    reputation: w.reputation / totalWeight,
    financial: w.financial / totalWeight,
    operational: w.operational / totalWeight
  };

  const weightedScore =
    categoryScores.security * normalizedWeights.security +
    categoryScores.reputation * normalizedWeights.reputation +
    categoryScores.financial * normalizedWeights.financial +
    categoryScores.operational * normalizedWeights.operational;

  return Math.round(weightedScore);
}

/**
 * Build an incident prevention result from incident, response, and weights
 *
 * @param incident - The incident that was responded to
 * @param response - The selected response option
 * @param weights - Optional scoring weights
 * @returns Complete IncidentPreventionResult
 */
export function buildIncidentPreventionResult(
  incident: Incident,
  response: ResponseOption,
  weights?: ScoringWeights | PreventionScoringWeights
): IncidentPreventionResult {
  const categoryScores = calculateCategoryPreventionScores(response.impact);
  const overallScore = calculateOverallPreventionScore(categoryScores, weights);

  return {
    incidentId: incident.id,
    incidentTitle: incident.title,
    selectedOptionId: response.id,
    selectedOptionLabel: response.label,
    categoryScores,
    overallScore,
    potentialImpact: incident.potentialImpact
  };
}

/**
 * Calculate cumulative prevention scores across all incident results
 *
 * @param incidentResults - Array of prevention results from each incident
 * @param weights - Optional scoring weights for overall calculation
 * @returns CumulativePreventionScores with averages and overall weighted score
 */
export function calculateCumulativePreventionScores(
  incidentResults: IncidentPreventionResult[],
  weights?: ScoringWeights | PreventionScoringWeights
): CumulativePreventionScores {
  if (incidentResults.length === 0) {
    return {
      categoryAverages: {
        security: 50,
        reputation: 50,
        financial: 50,
        operational: 50
      },
      overallWeightedScore: 50,
      incidentResults: []
    };
  }

  // Calculate averages for each category
  const categoryAverages: CategoryPreventionScore = {
    security: Math.round(
      incidentResults.reduce((sum, r) => sum + r.categoryScores.security, 0) / incidentResults.length
    ),
    reputation: Math.round(
      incidentResults.reduce((sum, r) => sum + r.categoryScores.reputation, 0) / incidentResults.length
    ),
    financial: Math.round(
      incidentResults.reduce((sum, r) => sum + r.categoryScores.financial, 0) / incidentResults.length
    ),
    operational: Math.round(
      incidentResults.reduce((sum, r) => sum + r.categoryScores.operational, 0) / incidentResults.length
    )
  };

  // Calculate overall weighted score from category averages
  const overallWeightedScore = calculateOverallPreventionScore(categoryAverages, weights);

  return {
    categoryAverages,
    overallWeightedScore,
    incidentResults
  };
}

/**
 * Get the color class for a prevention score
 *
 * @param score - Prevention score (0-100)
 * @param isLight - Whether using light theme
 * @returns Tailwind color class string
 */
export function getPreventionScoreColor(score: number, isLight: boolean): string {
  if (score >= 61) {
    return isLight ? 'text-green-600' : 'text-green-400';
  }
  if (score >= 31) {
    return isLight ? 'text-yellow-600' : 'text-yellow-400';
  }
  return isLight ? 'text-red-600' : 'text-red-400';
}

/**
 * Get the background color class for a prevention score bar
 *
 * @param score - Prevention score (0-100)
 * @returns Tailwind background color class string
 */
export function getPreventionScoreBarColor(score: number): string {
  if (score >= 61) return 'bg-green-500';
  if (score >= 31) return 'bg-yellow-500';
  return 'bg-red-500';
}

/**
 * Get the rating label for a prevention score
 *
 * @param score - Prevention score (0-100)
 * @returns Human-readable rating label
 */
export function getPreventionScoreRating(score: number): string {
  if (score >= 80) return 'Excellent';
  if (score >= 61) return 'Good';
  if (score >= 41) return 'Moderate';
  if (score >= 21) return 'Poor';
  return 'Critical';
}

/**
 * Get a description for the prevention level
 *
 * @param score - Prevention score (0-100)
 * @returns Description of what this prevention level means
 */
export function getPreventionDescription(score: number): string {
  if (score >= 80) return 'Response effectively prevents negative outcomes';
  if (score >= 61) return 'Response provides solid protection with minor gaps';
  if (score >= 41) return 'Response offers partial protection, room for improvement';
  if (score >= 21) return 'Response leaves significant vulnerabilities';
  return 'Response fails to address critical risks';
}

/**
 * Calculate prevention results for a list of incident responses
 *
 * @param responses - Array of incident responses
 * @param incidents - Array of incidents to match against
 * @param weights - Optional scoring weights
 * @returns Array of IncidentPreventionResult
 */
export function calculatePreventionResultsFromResponses(
  responses: IncidentResponse[],
  incidents: Incident[],
  weights?: ScoringWeights | PreventionScoringWeights
): IncidentPreventionResult[] {
  const results: IncidentPreventionResult[] = [];

  for (const response of responses) {
    const incident = incidents.find(i => i.id === response.incidentId);
    if (!incident) continue;

    // Handle timeout case
    if (response.selectedOptionId === 'timeout') {
      const timeoutScores: CategoryPreventionScore = {
        security: 0,
        reputation: 0,
        financial: 0,
        operational: 0
      };
      results.push({
        incidentId: incident.id,
        incidentTitle: incident.title,
        selectedOptionId: 'timeout',
        selectedOptionLabel: 'No Response (Timeout)',
        categoryScores: timeoutScores,
        overallScore: 0,
        potentialImpact: incident.potentialImpact
      });
      continue;
    }

    const selectedOption = incident.responseOptions.find(
      opt => opt.id === response.selectedOptionId
    );

    if (selectedOption) {
      results.push(buildIncidentPreventionResult(incident, selectedOption, weights));
    }
  }

  return results;
}
